import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "../lib/lang";
import { pages } from "../lib/pagesCopy";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { InkCap, InkBook, InkBriefcase, InkAward, InkSearch, InkPeople, InkChat } from "./InkIcons";

const P_ICONS = { ug: InkCap, pg: InkBook, executive: InkBriefcase, diploma: InkAward };

/* Home-page preview of programmes. Phone: swipeable cards. Laptop: a four-column row. */
export const ProgramsTeaser = () => {
  const { t, lang } = useLang();
  return (
    <section className="bg-white py-20 lg:py-28" data-testid="programs-teaser">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <Reveal><Eyebrow>{t.programs.eyebrow}</Eyebrow></Reveal>
            <Reveal delay={0.05}><SectionTitle>{t.programs.title}</SectionTitle></Reveal>
          </div>
          <Link to="/programmes" data-testid="teaser-all-programmes-link" className="hidden w-fit items-center gap-2 rounded-full border-2 border-ink px-6 py-3 font-display text-sm font-extrabold text-ink transition-colors hover:bg-ink hover:text-white lg:inline-flex">
            {pages[lang].teaser.all} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
        <ul className="-mx-5 mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 [scrollbar-width:none] lg:mx-0 lg:grid lg:grid-cols-4 lg:gap-5 lg:overflow-visible lg:px-0 lg:pb-0">
          {t.programs.items.map((p, i) => {
            const Icon = P_ICONS[p.key];
            const dark = i % 3 === 0;
            return (
              <li key={p.key} className="w-[78%] shrink-0 snap-start sm:w-[45%] lg:w-auto">
                <Link to="/programmes#programs" data-testid={`teaser-card-${p.key}`}
                  className={`group flex h-full flex-col rounded-[1.5rem] p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${dark ? "bg-ink text-white" : "border border-ink/10 bg-sky/60 text-ink"}`}>
                  <span className={`flex h-14 w-14 items-center justify-center rounded-[1.1rem_1.4rem_1rem_1.5rem] ${i % 2 ? "rotate-2" : "-rotate-2"} ${dark ? "bg-white/10 text-white" : "bg-ink text-white"}`}><Icon className="h-8 w-8" /></span>
                  <h3 className="mt-8 font-display text-xl font-extrabold">{p.title}</h3>
                  <p className={`mt-2 flex-1 text-sm leading-relaxed ${dark ? "text-white/85" : "text-night"}`}>{p.text}</p>
                  <ArrowUpRight className={`mt-6 h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${dark ? "text-sun" : "text-ink"}`} aria-hidden="true" />
                </Link>
              </li>
            );
          })}
        </ul>
        <Link to="/programmes" data-testid="teaser-all-programmes-link-mobile" className="mt-6 flex items-center justify-center gap-2 rounded-full bg-ink py-4 font-display font-extrabold text-white lg:hidden">
          {pages[lang].teaser.all} <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
};

const E_ICONS = [InkSearch, InkPeople, InkChat];

/* Signposts to the other pages. Phone: stacked rows. Laptop: three tall cards. */
export const ExplorePages = () => {
  const { lang } = useLang();
  const e = pages[lang].explore;
  return (
    <section className="bg-mist py-20 lg:py-28" data-testid="explore-pages">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><Eyebrow>{e.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.05}><SectionTitle>{e.title}</SectionTitle></Reveal>
        <div className="mt-12 grid gap-4 lg:grid-cols-3 lg:gap-5">
          {e.items.map((it, i) => {
            const Icon = E_ICONS[i];
            return (
              <Reveal key={it.to} delay={i * 0.06}>
                <Link to={it.to} data-testid={`explore-link-${it.to.slice(1)}`}
                  className="group flex h-full items-center gap-5 rounded-[1.5rem] border border-ink/10 bg-white p-5 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(18,24,63,0.45)] lg:flex-col lg:items-start lg:p-8">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-sun text-night"><Icon className="h-8 w-8" accent="#FFFFFF" /></span>
                  <span className="flex-1">
                    <span className="block font-display text-lg font-extrabold text-ink lg:mt-4 lg:text-2xl">{it.title}</span>
                    <span className="mt-1 block text-sm leading-relaxed text-night lg:mt-3 lg:text-base">{it.text}</span>
                  </span>
                  <ArrowRight className="h-5 w-5 shrink-0 text-ink transition-transform group-hover:translate-x-1 lg:mt-6" aria-hidden="true" />
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
